import * as React from "react";
import { Box, SvgIcon } from "@mui/material";
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import QuestionButton from "./../../../ui-components/Buttons/RedirectButton";
import { submitAnswer } from "../../../../store/slices/learner";
import { useDispatch } from "../../../../store";
import qs from "qs";

import MultipleChoice from "./multiple-choice";
import Identification from "./identification";
import FillTheBlank from "./fill-the-blank";
import Numeracy from "./numeracy";
import Enumerate from "./enumerate";
import MultipleSelect from "./multiple-select";
import Selection from "./selection";
import FactOrOpinion from "./fact-or-opinion";
import ListenAndAnswer from "./listen-and-answer";
import MatchText from "./match-text";
import Graph from "./graph";
import DragAndDrop from "./drag-and-drop";
import MatchTextSelection from "./match-text-selection";
import MultipleChoiceAudio from "./multiple-choice-audio";
import MultipleChoiceObjectAudio from "./multiple-choice-object-audio";
import CorrectOrder from "./correct-order";
import CheckBox from "./check-box";
import GridChoiceAudio from "./grid-choice-audio";
import MultipleChoiceImage from "./multiple-choice-image";
import MultipleTextInputs from "./multiple-text-inputs";
import FillTheBlankAlphabet from "./fill-in-blank-alphabet";
import DropDownSelect from "./drop-down-select";
import MultipleChoiceImageMultiple from "./multiple-choice-image-multiple";
import NewspaperIndex from "./newspaper-index";
import CorrectOrderMatch from "./correct-order-match";
import MultipleChoiceColumn from "./multiple-choice-column";
import Calendar from "./calendar";
import { ResourceLinks } from "../../../../constants";

export default function QuestionRenderer({
  question,
  nextQuestion = () => {},
}) {
  const dispatch = useDispatch();
  const questionData = question?.questionData || {};
  const [proceed, setProceed] = React.useState(false);
  const [answer, setAnswer] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [bodyOverflow, setBodyOverflow] = React.useState(true);
  const ref = React.useRef();
  const [startTime, setStartTime] = React.useState(Date.now());
  const questionAudioSrc = `${ResourceLinks.audio}instructions/mobile/${questionData?.questionAudio}`;
  const [playingQuestionUrl, setPlayingQuestionUrl] = React.useState("");
  const audioQuestionRef = React.useRef({});

  const { invite } = qs.parse(window.location.search, {
    ignoreQueryPrefix: true,
  });

  React.useEffect(() => {
    setProceed(false);
    ref.current.scrollTop = 0;
    setStartTime(Date.now());
  }, [question]);

  const handleOnAnswer = (data) => {
    setProceed(data?.canProceed || false);
    setAnswer(data?.answer || null);
  };

  const handleSubmitAnswer = async () => {
    setIsLoading(true);
    const nextQuestionId = await dispatch(
      submitAnswer(question, answer, startTime, invite)
    );
    nextQuestion(nextQuestionId);
    // setIsLoading(nextQuestionId !== "false");
    setIsLoading(false);
  };

  const handlePlayQuestionAudio = (url) => {
    const previousUrl = playingQuestionUrl;
    if (previousUrl !== "") {
        // we will set to empty to pause
        if (previousUrl !== url) {
          setPlayingQuestionUrl("");
          audioQuestionRef.current[previousUrl].pause();

            // set play again
            setPlayingQuestionUrl(url);
            audioQuestionRef.current[url].play();
        } else if (previousUrl === url) {
            // we will control the play or pause
            if (!audioQuestionRef.current[previousUrl].paused) {
              setPlayingQuestionUrl("");
              audioQuestionRef.current[previousUrl].pause();
            } else {
              setPlayingQuestionUrl(previousUrl);
              audioQuestionRef.current[previousUrl].play();
            }
        }
    } else {
        // play at initial
        setPlayingQuestionUrl(url);
        audioQuestionRef.current[url].play();
    }
    
  }

  const handleQuestionPlayEnded = () => {
    setPlayingQuestionUrl("");
  }

  const renderQuestionComponent = () => {
    const componentMap = {
      "multiple-choice": MultipleChoice,
      identification: Identification,
      "fill-the-blank": FillTheBlank,
      numeracy: Numeracy,
      enumerate: Enumerate,
      "multiple-select": MultipleSelect,
      selection: Selection,
      "fact-or-opinion": FactOrOpinion,
      "listen-and-answer": ListenAndAnswer,
      "match-text": MatchText,
      graph: Graph,
      "drag-and-drop": DragAndDrop,
      "match-text-selection": MatchTextSelection,
      "multiple-choice-audio": MultipleChoiceAudio,
      "multiple-choice-column": MultipleChoiceColumn,
      "correct-order": CorrectOrder,
      "correct-order-match": CorrectOrderMatch,
      "check-box": CheckBox,
      "grid-choice-audio": GridChoiceAudio,
      "multiple-choice-image": MultipleChoiceImage,
      "multiple-text-inputs": MultipleTextInputs,
      "fill-in-the-blank-alphabet": FillTheBlankAlphabet,
      "drop-down-select": DropDownSelect,
      "multiple-choice-image-multiple": MultipleChoiceImageMultiple,
      "newspaper-index": NewspaperIndex,
      "multiple-choice-object-audio": MultipleChoiceObjectAudio,
      "calendar": Calendar,
    };

    const Component = componentMap[questionData.questionType];
    return Component ? (
      <Component
        question={questionData}
        onAnswer={handleOnAnswer}
        onDrag={setBodyOverflow}
      />
    ) : null;
  };

  return (
    <React.Fragment>
      <Box
        className="question-text"
        style={questionData.questionTextStyle || {}}
      >
        <Box component="span">{questionData?.questionText || ""}</Box>
        {questionData?.questionAudio && (
          <React.Fragment>
            <SvgIcon component={playingQuestionUrl === "" ? PlayCircleFilledIcon : StopCircleIcon} fontSize="large" 
              onClick={() => handlePlayQuestionAudio(questionAudioSrc)}
            />
            <audio ref={(el) => audioQuestionRef.current[questionAudioSrc] = el} src={questionAudioSrc} onEnded={handleQuestionPlayEnded}/>
          </React.Fragment>
        )}
      </Box>
      <Box
        ref={ref}
        sx={{
          bgcolor: "white",
          height: "100%",
          width: { xs: "100%", sm: "calc(100% - 4px)" },
          overflow: bodyOverflow ? "auto" : "hidden",
        }}
      >
        {renderQuestionComponent()}
        <QuestionButton
          title="Next"
          disabled={!proceed}
          loading={isLoading}
          loadingMessage="Submitting.."
          handleClick={handleSubmitAnswer}
        />
      </Box>
    </React.Fragment>
  );
}
